/**
 * MfaStatusGrid — MFA enrollment status per user (B.8)
 */

import { useMfaStatus } from '../../hooks/useSecurity';
import { Loader2, ShieldCheck, ShieldX } from 'lucide-react';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function MfaStatusGrid() {
  const { data, isLoading } = useMfaStatus();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
      </div>
    );
  }

  if (!data) return null;

  const { users, summary } = data;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ems-text-primary">MFA Status</h3>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${summary.percentage >= 100 ? 'bg-ems-green' : summary.percentage >= 50 ? 'bg-ems-amber' : 'bg-ems-red'}`} />
            <span className="text-xs text-ems-text-muted">
              {summary.mfa_enabled} of {summary.total} users ({summary.percentage}%)
            </span>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-ems-bg rounded-full mb-4 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            summary.percentage >= 100 ? 'bg-ems-green' :
            summary.percentage >= 50 ? 'bg-ems-amber' : 'bg-ems-red'
          }`}
          style={{ width: `${summary.percentage}%` }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {users.map((u) => (
          <div
            key={u.user_id}
            className={`flex items-center gap-3 p-3 rounded-lg border ${
              u.mfa_enabled ? 'border-ems-border bg-ems-bg-card' : 'border-ems-red/20 bg-ems-red/5'
            }`}
          >
            <div className="flex-shrink-0">
              {u.mfa_enabled ? (
                <ShieldCheck className="w-5 h-5 text-ems-green" />
              ) : (
                <ShieldX className="w-5 h-5 text-ems-red" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-ems-text-primary truncate">
                {u.first_name || u.last_name
                  ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                  : u.email}
              </p>
              <p className="text-[10px] text-ems-text-muted truncate">{u.email}</p>
              <p className="text-[10px] text-ems-text-muted">Last login: {formatDate(u.last_login)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
